import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import { useAuth } from "../../contexts/AuthContext";
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
} from "@mui/material";
import { Save as SaveIcon, Cancel as CancelIcon, Lock as LockIcon, WarningAmber as WarningAmberIcon } from "@mui/icons-material";
import { AlertTitle } from "@mui/material";
import { Chip, Avatar } from "@mui/material";
import ClienteSelect from "./ClienteSelect";
import ColaboradoresMultiselect from "./ColaboradoresMultiselect";
import ServiciosDataTable from "./ServiciosDataTable";
import PaquetesDataTable from "./PaquetesDataTable";
import {
  getEventoValidationSchema,
  getInitialEventoValues,
  transformEventoToForm,
  transformFormToEvento,
  TIPOS_EVENTO,
} from "../../utils/eventoValidation";
import api from "../../services/api";

/**
 * EventForm - Componente principal para crear/editar eventos
 * Orquesta todos los componentes relacionales
 */
const EventForm = ({ eventoId = null, onSuccess, onCancel, lockedColaboradores = null }) => {
  const { isAdmin } = useAuth();
  // Si hay colaboradores bloqueados (vista colaborador), pre-llenar sus IDs
  const lockedIds = lockedColaboradores ? lockedColaboradores.map((c) => c.id) : null;
  const [initialValues, setInitialValues] = useState(() => ({
    ...getInitialEventoValues(),
    ...(lockedIds ? { colaboradores_ids: lockedIds } : {}),
  }));
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  // Conflicto de fecha: verificar si ya hay eventos ese día
  const [conflictoFecha, setConflictoFecha] = useState({ count: 0, eventos: [] });
  const [checkingConflicto, setCheckingConflicto] = useState(false);

  const checkFechaConflicto = async (fechaStr) => {
    if (!fechaStr) { setConflictoFecha({ count: 0, eventos: [] }); return; }
    try {
      setCheckingConflicto(true);
      const mes = String(fechaStr).slice(0, 7);
      const res = await api.get(`/disponibilidad?mes=${mes}`);
      const fechas = res.data?.fechas ?? [];
      const info = fechas.find((f) => f.fecha === fechaStr);
      setConflictoFecha({ count: info?.count ?? 0, eventos: info?.eventos ?? [] });
    } catch {
      setConflictoFecha({ count: 0, eventos: [] });
    } finally {
      setCheckingConflicto(false);
    }
  };

  const steps = [
    "Información Básica",
    "Cliente & Colaboradores",
    "Servicios & Paquetes",
    "Financiero & Resumen",
  ];

  // Cargar evento si es edición
  useEffect(() => {
    if (eventoId) {
      loadEvento();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  const loadEvento = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/eventos/${eventoId}?include=cliente,colaboradores,servicios,insumos`,
      );
      const formValues = transformEventoToForm(response.data);
      setInitialValues(formValues);
    } catch (error) {
      setErrors({ general: "Error al cargar el evento" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      setErrors({});

      const eventoData = transformFormToEvento(values, eventoId);
      const endpoint = eventoId ? `/eventos/${eventoId}` : "/eventos";
      const method = eventoId ? "PUT" : "POST";

      const response = await api[method === "POST" ? "post" : "put"](
        endpoint,
        eventoData,
      );

      setSuccessMessage(
        eventoId
          ? "Evento actualizado correctamente"
          : "Evento creado correctamente",
      );

      setTimeout(() => {
        if (onSuccess) {
          onSuccess(response.data);
        }
      }, 1500);
    } catch (error) {
      setErrors({
        general: error.response?.data?.message || "Error al guardar el evento",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        {/* Encabezado */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ mb: 1 }}>
            {eventoId ? "Editar Evento" : "Crear Nuevo Evento"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Completa los datos del evento, colaboradores y servicios
          </Typography>
        </Box>

        {/* Mensajes de error/éxito */}
        {errors.general && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrors({})}>
            {errors.general}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Formik
          initialValues={initialValues}
          validationSchema={getEventoValidationSchema(isAdmin)}
          onSubmit={handleSubmit}
          enableReinitialize={true}
          validateOnMount={true}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({
            values,
            errors: formErrors,
            touched,
            setFieldValue,
            setValues,
            isValid,
          }) => (
            <Form>
              {/* Paso 1: Información Básica */}
              {activeStep === 0 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Información Básica del Evento
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {/* Tipo de Evento */}
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        sx={{ minWidth: 200 }}
                        error={touched.tipo_evento && !!formErrors.tipo_evento}
                      >
                        <InputLabel shrink>Tipo de Evento</InputLabel>
                        <Select
                          name="tipo_evento"
                          label="Tipo de Evento"
                          notched={true}
                          value={values.tipo_evento || ""}
                          onChange={(e) =>
                            setFieldValue("tipo_evento", e.target.value)
                          }
                        >
                          <MenuItem value="">
                            <em>Selecciona un tipo de evento</em>
                          </MenuItem>
                          {TIPOS_EVENTO.map((tipo) => (
                            <MenuItem key={tipo} value={tipo}>
                              {tipo}
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.tipo_evento && formErrors.tipo_evento && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 0.5, display: "block" }}
                          >
                            {formErrors.tipo_evento}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    {/* Título del Evento */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="titulo"
                        label="Título del Evento (opcional)"
                        placeholder="Ej: XV Años de María, Boda García-López"
                        value={values.titulo || ""}
                        onChange={(e) =>
                          setFieldValue("titulo", e.target.value)
                        }
                        helperText="Nombre específico para identificar el evento"
                      />
                    </Grid>

                    {/* Descripción */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        name="descripcion"
                        label="Descripción"
                        value={values.descripcion}
                        onChange={(e) =>
                          setFieldValue("descripcion", e.target.value)
                        }
                        error={touched.descripcion && !!formErrors.descripcion}
                        helperText={
                          touched.descripcion && formErrors.descripcion
                        }
                      />
                    </Grid>

                    {/* Fecha Inicio */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="date"
                        name="fecha_inicio"
                        label="Fecha de Inicio"
                        value={values.fecha_inicio.toISOString().split("T")[0]}
                        onChange={(e) => {
                          const nuevaFechaInicio = new Date(e.target.value + "T12:00:00");
                          setFieldValue("fecha_inicio", nuevaFechaInicio);
                          if (values.fecha_fin < nuevaFechaInicio) {
                            setFieldValue("fecha_fin", nuevaFechaInicio);
                          }
                          // Verificar conflicto de disponibilidad
                          checkFechaConflicto(e.target.value);
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: isAdmin
                            ? "2026-01-01"
                            : new Date(
                                new Date().getTime() + 24 * 60 * 60 * 1000,
                              )
                                .toISOString()
                                .split("T")[0],
                        }}
                        error={
                          touched.fecha_inicio && !!formErrors.fecha_inicio
                        }
                        helperText={
                          touched.fecha_inicio && formErrors.fecha_inicio
                        }
                      />
                    </Grid>

                    {/* Hora Inicio */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="time"
                        name="hora_inicio"
                        label="Hora de Inicio"
                        value={values.hora_inicio}
                        onChange={(e) =>
                          setFieldValue("hora_inicio", e.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        error={touched.hora_inicio && !!formErrors.hora_inicio}
                        helperText={
                          touched.hora_inicio && formErrors.hora_inicio
                        }
                      />
                    </Grid>

                    {/* Fecha Fin */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="date"
                        name="fecha_fin"
                        label="Fecha de Fin"
                        value={values.fecha_fin.toISOString().split("T")[0]}
                        onChange={(e) =>
                          setFieldValue("fecha_fin", new Date(e.target.value))
                        }
                        InputLabelProps={{ shrink: true }}
                        error={touched.fecha_fin && !!formErrors.fecha_fin}
                        helperText={touched.fecha_fin && formErrors.fecha_fin}
                      />
                    </Grid>

                    {/* Hora Fin */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="time"
                        name="hora_fin"
                        label="Hora de Fin"
                        value={values.hora_fin}
                        onChange={(e) =>
                          setFieldValue("hora_fin", e.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        error={touched.hora_fin && !!formErrors.hora_fin}
                        helperText={touched.hora_fin && formErrors.hora_fin}
                      />
                    </Grid>

                    {/* ── Alerta de conflicto de disponibilidad ── */}
                    {conflictoFecha.count > 0 && (
                      <Grid item xs={12}>
                        <Alert
                          severity="warning"
                          icon={<WarningAmberIcon />}
                          sx={{
                            borderRadius: "12px",
                            bgcolor: "rgba(245,158,11,0.08)",
                            border: "1.5px solid rgba(245,158,11,0.45)",
                            color: "#fbbf24",
                            "& .MuiAlert-icon": { color: "#f59e0b", alignItems: "flex-start", pt: 0.3 },
                            "& .MuiAlert-message": { width: "100%" },
                          }}
                        >
                          <AlertTitle sx={{ fontWeight: 800, color: "#f59e0b", mb: 0.5 }}>
                            ¡Atención! Ya existe{conflictoFecha.count > 1 ? "n" : ""}{" "}
                            {conflictoFecha.count} evento{conflictoFecha.count > 1 ? "s" : ""} programado{conflictoFecha.count > 1 ? "s" : ""} en esta fecha
                          </AlertTitle>
                          <Typography variant="body2" sx={{ color: "#fde68a", mb: 1, fontSize: "0.82rem" }}>
                            Verifica la <strong>disponibilidad de servicios y productos</strong> antes de confirmar este evento para evitar conflictos de cobertura.
                          </Typography>
                          {conflictoFecha.eventos.length > 0 && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                              {conflictoFecha.eventos.map((ev, i) => (
                                <Box key={ev.id || i} sx={{
                                  display: "flex", alignItems: "center", gap: 1,
                                  px: 1.5, py: 0.6, borderRadius: "8px",
                                  bgcolor: "rgba(245,158,11,0.10)",
                                  border: "1px solid rgba(245,158,11,0.20)",
                                }}>
                                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#f59e0b", flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#fde68a", flex: 1 }} noWrap>
                                    {ev.titulo || `Evento #${ev.id}`}
                                  </Typography>
                                  {ev.hora_inicio && (
                                    <Typography sx={{ fontSize: "0.7rem", color: "#f59e0b", flexShrink: 0 }}>
                                      {ev.hora_inicio}{ev.hora_fin ? ` – ${ev.hora_fin}` : ""}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Alert>
                      </Grid>
                    )}
                    {checkingConflicto && (
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CircularProgress size={14} sx={{ color: "#6366f1" }} />
                          <Typography variant="caption" sx={{ color: "text.disabled" }}>Verificando disponibilidad…</Typography>
                        </Box>
                      </Grid>
                    )}

                    {/* Ubicación */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        name="ubicacion"
                        label="Ubicación/Lugar"
                        placeholder="Ej: Salón Los Arcos"
                        value={values.ubicacion}
                        onChange={(e) =>
                          setFieldValue("ubicacion", e.target.value)
                        }
                        error={touched.ubicacion && !!formErrors.ubicacion}
                        helperText={touched.ubicacion && formErrors.ubicacion}
                      />
                    </Grid>

                    {/* Dirección */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        name="direccion"
                        label="Dirección Completa"
                        placeholder="Ej: Av. Principal #123, Col. Centro"
                        value={values.direccion || ""}
                        onChange={(e) =>
                          setFieldValue("direccion", e.target.value)
                        }
                        helperText="Calle, número, colonia, ciudad"
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Paso 2: Cliente & Colaboradores */}
              {activeStep === 1 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Cliente y Equipo de Trabajo
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {/* Cliente */}
                    <Grid item xs={12}>
                      <ClienteSelect
                        value={values.cliente_id}
                        onChange={(id) => setFieldValue("cliente_id", id)}
                        error={touched.cliente_id && !!formErrors.cliente_id}
                        helperText={touched.cliente_id && formErrors.cliente_id}
                      />
                    </Grid>

                    {/* Colaboradores */}
                    <Grid item xs={12}>
                      {lockedColaboradores ? (
                        /* Vista colaborador: solo muestra su nombre, no se puede cambiar */
                        <Box
                          sx={{
                            border: "1px solid rgba(255,255,255,0.23)",
                            borderRadius: 1,
                            p: 1.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <LockIcon sx={{ fontSize: 16, color: "text.secondary", mr: 0.5 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                            Colaborador asignado:
                          </Typography>
                          {lockedColaboradores.map((c) => (
                            <Chip
                              key={c.id}
                              avatar={
                                <Avatar
                                  sx={{
                                    bgcolor: "#00C9FF40",
                                    color: "#00C9FF",
                                    width: 24,
                                    height: 24,
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  {c.nombre_completo?.charAt(0).toUpperCase()}
                                </Avatar>
                              }
                              label={c.nombre_completo}
                              variant="outlined"
                              sx={{ borderColor: "#00C9FF60", color: "#00C9FF" }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <ColaboradoresMultiselect
                          value={values.colaboradores_ids}
                          onChange={(ids) =>
                            setFieldValue("colaboradores_ids", ids)
                          }
                          error={
                            touched.colaboradores_ids &&
                            !!formErrors.colaboradores_ids
                          }
                          helperText={
                            touched.colaboradores_ids &&
                            formErrors.colaboradores_ids
                          }
                          maxSelections={20}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Paso 3: Servicios, Paquetes & Insumos */}
              {activeStep === 2 && (
                <Box>
                  <Grid container spacing={3}>
                    {/* Servicios */}
                    <Grid item xs={12}>
                      <ServiciosDataTable
                        value={values.servicios}
                        onChange={(servicios) => {
                          const primerServicioId = servicios.length > 0 ? (servicios[0].servicio_id || null) : null;
                          const serviciosTotal = servicios.reduce(
                            (sum, s) => sum + (s.total || 0),
                            0,
                          );
                          const paquetesTotal = values.paquetes.reduce(
                            (sum, p) => sum + (p.total || 0),
                            0,
                          );
                          const insumosTotal = values.insumos.reduce(
                            (sum, i) => sum + (i.subtotal || 0),
                            0,
                          );
                          const total =
                            serviciosTotal + paquetesTotal + insumosTotal;
                          // Actualizar todos los campos en una sola operación para evitar
                          // condiciones de carrera en la validación de Formik
                          setValues({
                            ...values,
                            servicios,
                            tipo_servicio_id: primerServicioId,
                            monto_paquete: total,
                          });
                        }}
                        error={touched.servicios && !!formErrors.servicios}
                        helperText={touched.servicios && formErrors.servicios}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider />
                    </Grid>

                    {/* Paquetes */}
                    <Grid item xs={12}>
                      <PaquetesDataTable
                        value={values.paquetes}
                        onChange={(paquetes) => {
                          const primerPaqueteId = paquetes.length > 0 ? (paquetes[0].paquete_id || null) : null;
                          const serviciosTotal = values.servicios.reduce(
                            (sum, s) => sum + (s.total || 0),
                            0,
                          );
                          const paquetesTotal = paquetes.reduce(
                            (sum, p) => sum + (p.total || 0),
                            0,
                          );
                          const insumosTotal = values.insumos.reduce(
                            (sum, i) => sum + (i.subtotal || 0),
                            0,
                          );
                          const total =
                            serviciosTotal + paquetesTotal + insumosTotal;
                          // Actualizar todos los campos en una sola operación para evitar
                          // condiciones de carrera en la validación de Formik
                          setValues({
                            ...values,
                            paquetes,
                            paquete_id: primerPaqueteId,
                            monto_paquete: total,
                          });
                        }}
                        error={touched.paquetes && !!formErrors.paquetes}
                        helperText={touched.paquetes && formErrors.paquetes}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Paso 4: Financiero & Resumen */}
              {activeStep === 3 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Información Financiera
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {/* Monto Paquete (Calculado) */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        name="monto_paquete"
                        label="Monto del Paquete"
                        value={values.monto_paquete}
                        disabled
                        InputProps={{ startAdornment: "$" }}
                        helperText="Se calcula automáticamente con servicios, paquetes e insumos"
                      />
                    </Grid>

                    {/* Adelanto */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        name="adelanto"
                        label="Adelanto"
                        value={values.adelanto}
                        onChange={(e) => {
                          const val = e.target.value;
                          // Permitir vacío temporalmente, convertir a número al perder foco
                          setFieldValue(
                            "adelanto",
                            val === "" ? "" : parseFloat(val),
                          );
                        }}
                        onBlur={(e) => {
                          // Al perder foco, si está vacío convertir a 0
                          if (
                            values.adelanto === "" ||
                            isNaN(values.adelanto)
                          ) {
                            setFieldValue("adelanto", 0);
                          }
                        }}
                        InputProps={{ startAdornment: "$" }}
                        inputProps={{ min: 500 }}
                        error={touched.adelanto && !!formErrors.adelanto}
                        helperText={touched.adelanto && formErrors.adelanto}
                      />
                    </Grid>

                    {/* Pendiente de Pago (Calculado) */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Pendiente de Pago"
                        value={Math.max(
                          0,
                          values.monto_paquete - values.adelanto,
                        )}
                        disabled
                        InputProps={{ startAdornment: "$" }}
                        helperText="Monto del Paquete - Adelanto"
                      />
                    </Grid>

                    {/* Estado */}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Estado</InputLabel>
                        <Select
                          name="estado"
                          value={values.estado}
                          onChange={(e) =>
                            setFieldValue("estado", e.target.value)
                          }
                          label="Estado"
                          error={touched.estado && !!formErrors.estado}
                        >
                          <MenuItem value="Por Confirmar">
                            Por Confirmar
                          </MenuItem>
                          <MenuItem value="Confirmado">Confirmado</MenuItem>
                          <MenuItem value="Completado">Completado</MenuItem>
                          <MenuItem value="Día Laborado">Día Laborado</MenuItem>
                          <MenuItem value="Cancelado">Cancelado</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Notas */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        name="notas"
                        label="Notas Adicionales"
                        value={values.notas}
                        onChange={(e) => setFieldValue("notas", e.target.value)}
                        error={touched.notas && !!formErrors.notas}
                        helperText={touched.notas && formErrors.notas}
                      />
                    </Grid>
                  </Grid>

                  {/* Resumen */}
                  <Paper
                    sx={{
                      p: 3,
                      bgcolor: "#131829",
                      border: "1px solid rgba(0, 201, 255, 0.2)",
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, color: "#00C9FF", fontWeight: 600 }}
                    >
                      Resumen Financiero
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={3} sx={{ textAlign: "center" }}>
                        <Typography variant="body2" sx={{ color: "#B0BEC5" }}>
                          Monto del Paquete:
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ color: "#FFFFFF", fontWeight: 600 }}
                        >
                          ${values.monto_paquete.toLocaleString("es-CO")}
                        </Typography>
                      </Grid>
                      <Grid item xs={3} sx={{ textAlign: "center" }}>
                        <Typography variant="body2" sx={{ color: "#B0BEC5" }}>
                          Adelanto:
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ color: "#92FE9D", fontWeight: 600 }}
                        >
                          ${values.adelanto.toLocaleString("es-CO")}
                        </Typography>
                      </Grid>
                      <Grid item xs={3} sx={{ textAlign: "center" }}>
                        <Typography variant="body2" sx={{ color: "#B0BEC5" }}>
                          Pendiente de Pago:
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            color:
                              values.monto_paquete - values.adelanto > 0
                                ? "#FF5252"
                                : "#92FE9D",
                            fontWeight: 600,
                          }}
                        >
                          $
                          {(
                            values.monto_paquete - values.adelanto
                          ).toLocaleString("es-CO")}
                        </Typography>
                      </Grid>
                      <Grid item xs={3} sx={{ textAlign: "center" }}>
                        <Typography variant="body2" sx={{ color: "#B0BEC5" }}>
                          Colaboradores Asignados:
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ color: "#CE93D8", fontWeight: 600 }}
                        >
                          {values.colaboradores_ids.length}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Mostrar errores de validación si existen */}
                  {!isValid && Object.keys(formErrors).length > 0 && (
                    <Alert
                      severity="warning"
                      sx={{
                        mt: 2,
                        bgcolor: "rgba(255, 193, 7, 0.1)",
                        border: "1px solid rgba(255, 193, 7, 0.3)",
                        "& .MuiAlert-message": { color: "#FFC107" },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Campos con errores de validación:
                      </Typography>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {Object.entries(formErrors).map(([field, error]) => (
                          <li key={field} style={{ color: "#FFE082" }}>
                            <strong>{field}:</strong>{" "}
                            {typeof error === "string"
                              ? error
                              : "Error de validación"}
                          </li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                </Box>
              )}

              {/* Botones de navegación */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 4,
                }}
              >
                <Box>
                  <Button
                    variant="outlined"
                    disabled={activeStep === 0}
                    onClick={handleBack}
                  >
                    Atrás
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={activeStep === steps.length - 1}
                    onClick={handleNext}
                    sx={{ ml: 1 }}
                  >
                    Siguiente
                  </Button>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={onCancel}
                    disabled={submitting}
                    sx={{ mr: 1 }}
                  >
                    Cancelar
                  </Button>
                  {activeStep === steps.length - 1 && (
                    <Tooltip
                      title={
                        !isValid && Object.keys(formErrors).length > 0
                          ? `Errores: ${Object.keys(formErrors).join(", ")}`
                          : ""
                      }
                      arrow
                    >
                      <span>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<SaveIcon />}
                          type="submit"
                          disabled={submitting || !isValid}
                          sx={{
                            background: isValid
                              ? "linear-gradient(45deg, #00C9FF 30%, #92FE9D 90%)"
                              : undefined,
                            "&:disabled": {
                              bgcolor: "rgba(255,255,255,0.12)",
                              color: "rgba(255,255,255,0.5)",
                            },
                          }}
                        >
                          {submitting ? (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                          ) : null}
                          {eventoId ? "Actualizar" : "Crear"} Evento
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Paper>
    </Container>
  );
};

export default EventForm;
